/**
 * Secure Storage Service
 *
 * Platform-specific secure credential storage for saved hub connections.
 *
 * Backends:
 *  - Electron:   safeStorage API (OS-level encryption)
 *  - Capacitor:  @capacitor/preferences (device-local, keychain-backed on iOS)
 *  - Web:        localStorage (NOT secure — logs a warning)
 *
 * All keys are namespaced with "otto_creds_" prefix.
 */

import { isElectron, isCapacitor } from '../config/runtime';

const KEY_PREFIX = 'otto_creds_';

// ─────────────────────────────────────────────────────────────────────────────
// Storage backends
// ─────────────────────────────────────────────────────────────────────────────

async function webStore(key: string, value: string): Promise<void> {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(KEY_PREFIX + key, value);
  }
}

async function webGet(key: string): Promise<string | null> {
  if (typeof localStorage !== 'undefined') {
    return localStorage.getItem(KEY_PREFIX + key);
  }
  return null;
}

async function webDelete(key: string): Promise<void> {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(KEY_PREFIX + key);
  }
}

async function capacitorStore(key: string, value: string): Promise<void> {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key: KEY_PREFIX + key, value });
  } catch (e) {
    console.warn('[SecureStorage] Capacitor Preferences.set failed:', e);
    // Fallback to localStorage on error
    await webStore(key, value);
  }
}

async function capacitorGet(key: string): Promise<string | null> {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key: KEY_PREFIX + key });
    return value ?? null;
  } catch (e) {
    console.warn('[SecureStorage] Capacitor Preferences.get failed:', e);
    return webGet(key);
  }
}

async function capacitorDelete(key: string): Promise<void> {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.remove({ key: KEY_PREFIX + key });
  } catch (e) {
    console.warn('[SecureStorage] Capacitor Preferences.remove failed:', e);
    await webDelete(key);
  }
}

async function electronStore(key: string, value: string): Promise<void> {
  const electron = window.electron;
  if (electron?.storeSecure) {
    try {
      await electron.storeSecure(KEY_PREFIX + key, value);
      return;
    } catch (e) {
      // OS-level encryption unavailable (e.g. Linux without keyring) — do NOT
      // fall back to plaintext localStorage; credentials simply won't persist.
      console.warn('[SecureStorage] Electron safeStorage unavailable — credential not persisted:', e);
      return;
    }
  }
  // No IPC bridge (e.g. Vite dev server in a normal browser): fall back to
  // localStorage with a warning.
  console.warn('[SecureStorage] Electron safeStorage API missing, falling back to localStorage');
  await webStore(key, value);
}

async function electronGet(key: string): Promise<string | null> {
  const electron = window.electron;
  if (electron?.getSecure) {
    try {
      const value = await electron.getSecure(KEY_PREFIX + key);
      if (value !== null && value !== undefined) return value;
    } catch (e) {
      console.warn('[SecureStorage] Electron getSecure failed:', e);
    }
    // One-time migration of credentials stored by older builds in localStorage.
    const legacy = await webGet(key);
    if (legacy !== null && legacy !== undefined) {
      if (electron?.storeSecure) {
        try {
          await electron.storeSecure(KEY_PREFIX + key, legacy);
          await webDelete(key);
        } catch (e) {
          console.warn('[SecureStorage] Legacy credential migration failed:', e);
        }
      }
      return legacy;
    }
    return null;
  }
  return webGet(key);
}

async function electronDelete(key: string): Promise<void> {
  const electron = window.electron;
  if (electron?.deleteSecure) {
    try {
      await electron.deleteSecure(KEY_PREFIX + key);
    } catch (e) {
      console.warn('[SecureStorage] Electron deleteSecure failed:', e);
    }
  }
  // Always clear any legacy plaintext copy too.
  await webDelete(key);
}

// ─────────────────────────────────────────────────────────────────────────────
// Detection
// ─────────────────────────────────────────────────────────────────────────────

function getBackend(): 'electron' | 'capacitor' | 'web' {
  if (isElectron()) return 'electron';
  if (isCapacitor()) return 'capacitor';
  return 'web';
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Store a value securely.
 * @param key - Unique key (will be namespaced internally)
 * @param value - String value to store
 */
export async function storeSecure(key: string, value: string): Promise<void> {
  const backend = getBackend();
  switch (backend) {
    case 'electron':
      return electronStore(key, value);
    case 'capacitor':
      return capacitorStore(key, value);
    default:
      console.warn(
        '[SecureStorage] Using localStorage for credentials — NOT secure on web. Consider using HTTPS + HttpOnly cookies for web deployments.',
      );
      return webStore(key, value);
  }
}

/**
 * Retrieve a securely stored value.
 * @param key - Unique key
 * @returns The stored value, or null if not found
 */
export async function getSecure(key: string): Promise<string | null> {
  const backend = getBackend();
  switch (backend) {
    case 'electron':
      return electronGet(key);
    case 'capacitor':
      return capacitorGet(key);
    default:
      return webGet(key);
  }
}

/**
 * Delete a securely stored value.
 * @param key - Unique key
 */
export async function deleteSecure(key: string): Promise<void> {
  const backend = getBackend();
  switch (backend) {
    case 'electron':
      return electronDelete(key);
    case 'capacitor':
      return capacitorDelete(key);
    default:
      return webDelete(key);
  }
}
